// src/App.jsx - CORRECTED IMPORTS AND ROUTES
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Routes, Route, useNavigate, Navigate, useLocation } from 'react-router-dom';
// --- Service and Firebase Imports ---
import { auth, db } from './firebase';
import { doc, getDoc } from 'firebase/firestore';
import userService from './services/enhancedUserService.js';
import { sendVerificationEmail, sendWelcomeEmail, updateUserProfile } from './utils/authUtils';
// --- Service and Firebase Imports ---
import { onAuthStateChanged, signOut, signInWithEmailAndPassword, createUserWithEmailAndPassword, updateProfile, applyActionCode } from 'firebase/auth';
import { clearGuestExamPending } from './utils/onboardingStorage';
import { clearAnonExamSessions } from './utils/sessionStore.js';
import examService from './services/examService';
import studyPlanService from './services/studyPlanService';
import PresenceProvider from './components/PresenceProvider';
import websiteTimeTracker from './services/websiteTimeTracker';
import { useTranslation } from 'react-i18next';
import { isSupportedLang } from './i18n';
// --- Layouts and Pages ---
import LangRoute from './components/layout/LangRoute';
import {
  detectUserLanguage,
  isFirstVisit,
  setLanguagePreference,
  getStoredLanguage
} from './utils/languageDetector';
import {
  transformImmediateData,
  loadGrammarData,
  loadVocabularyData,
  loadActivityData
} from './utils/dashboardDataTransformer';
import CompactDashboard from './components/dashboard/CompactDashboard';
import TermsOfServicePage from './pages/TermsOfServicePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import './styles/mobileGrammar.css';
import './styles/partyGame.css';
import './styles/top10Game.css';
import FaqPage from './pages/FaqPage';
import AppLayout from './components/layout/AppLayout';
import RequireOnboarding from './components/layout/RequireOnboarding';
import BlockOnboardingRerun from './components/layout/BlockOnboardingRerun';
import PublicLayout from './components/layout/PublicLayout';
import AuthPage from './pages/AuthPage';
import OnboardingPage from './pages/OnboardingPage';
import HomePage from './pages/HomePage';
import HomePageB from './pages/HomePageB';
import NotFoundPage from './pages/NotFoundPage';
import TelcB1LandingPage from './pages/TelcB1LandingPage';
import TelcB2LandingPage from './pages/TelcB2LandingPage';
import TryExamPage from './pages/TryExamPage';
import MockExamIndexPage from './pages/MockExamIndexPage';
import MockExamLandingPage from './pages/MockExamLandingPage';
// Paper exam booklet preview: dev only. import.meta.env.DEV is false in
// production builds, so this import is dropped from the bundle entirely.
// Win-back survey from Anna's email (/:lang/survey/:token); loaded only when opened.
const SurveyPage = React.lazy(() => import('./pages/SurveyPage'));
const FounderPortfolioPage = React.lazy(() => import('./pages/FounderPortfolioPage'));
const PaperExamPreviewPage = import.meta.env.DEV
  ? React.lazy(() => import('./pages/PaperExamPreviewPage'))
  : null;
// Party games are dev-only (see GAMES_ENABLED in config/featureFlags.js).
const PartyGamePage = import.meta.env.DEV
  ? React.lazy(() => import('./pages/PartyGamePage'))
  : null;
import GrammarTopicPage from './pages/GrammarTopicPage';
import { GrammarHubPage, GrammarLevelPage } from './pages/GrammarIndexPages';

/** Where to send an already-authenticated visit to /auth. Honors the one-shot
 *  'logami:postAuthRedirect' localStorage key (set e.g. by the guest exam
 *  signup gate) so mid-flow signups return to their flow instead of the
 *  dashboard. localStorage (not a ?redirect param) because the Google OAuth
 *  redirect flow reloads the page and drops query params. */
const PostAuthRedirect = ({ currentLang }) => {
  // Read-only during render: StrictMode double-invokes initializers in dev, so
  // consuming the key here would make the second pass fall back to /dashboard.
  // The key is deleted after the redirect has committed.
  const [target] = useState(() => {
    try {
      const raw = window.localStorage.getItem('logami:postAuthRedirect');
      return raw && raw.startsWith('/') && !raw.startsWith('//') ? raw : null;
    } catch {
      return null;
    }
  });
  useEffect(() => {
    if (!target) return;
    try {
      window.localStorage.removeItem('logami:postAuthRedirect');
    } catch {
      // ignore
    }
  }, [target]);
  return <Navigate to={`/${currentLang}${target || '/dashboard'}`} replace />;
};
/** Signed-out visitor on an app page (a bookmark, an email link): remember the page for
 *  PostAuthRedirect, so after signing in they land there instead of on the dashboard. */
const SignInFirst = ({ currentLang }) => {
  const { pathname, search } = useLocation();
  const target = pathname.replace(/^\/[a-z]{2}(?=\/|$)/, '') + search;
  if (target.startsWith('/') && target !== '/' && target !== '/dashboard') {
    try { window.localStorage.setItem('logami:postAuthRedirect', target); } catch { /* ignore */ }
  }
  return <Navigate to={`/${currentLang}/auth`} replace />;
};
import { getLandingPageVariant } from './utils/experiments';
import ExamDetailPage from './components/practice/ExamDetailPage.jsx';
import ExamSimulatorPage from './components/practice/ExamSimulatorPage.jsx';
import ExamResultsPage from './components/practice/ExamResultsPage.jsx';
import PracticePage from './pages/PracticePage';
import RedemittelPage from './pages/RedemittelPage.jsx';
import ExamSectionPractice from './components/practice/ExamSectionPractice';
import SettingsPage from './pages/SettingsPage';
import ProfilePage from './pages/ProfilePage';
import ProfileEditPage from './pages/ProfileEditPage';
import StudyPlan from './components/study/StudyPlan';
import GrammarLearningContainer from './components/grammar/GrammarLearningContainer';
import StudyPlanWizard from './components/study/StudyPlanWizard';
import ExamInstructionsPage from './components/practice/ExamInstructionsPage.jsx';
import VocabularyPage from './components/vocabulary/VocabularyPage.jsx';
import GrammarPage from './components/grammar/GrammarPage.jsx';
import ResourceHubPage from './pages/ResourceHubPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import AboutUsPage from './pages/AboutUsPage';
import ContactPage from './pages/ContactPage';
import PricingPage from './pages/PricingPage';
import CheckoutPage from './pages/CheckoutPage';
import SubscriptionManagementPage from './pages/SubscriptionManagementPage';
import StudyPlanPage from './components/study/StudyPlanPage.jsx';
import VerifiedPage from './pages/VerifiedPage';
import ConsultationBookingPage from './pages/ConsultationBookingPage';
import { usePWA } from './hooks/usePWA';
import NetworkStatus from './components/layout/NetworkStatus';
import storageService from './services/storageService';
import { logDailyLogin } from './utils/activityLogger';
import ErrorBoundary from './components/error/ErrorBoundary';
import ExamStructurePage from './components/practice/ExamStructurePage.jsx';
import LesenTrainingPage from './pages/LesenTrainingPage';
import HorenTrainingPage from './pages/HorenTrainingPage';
import SchreibenTrainingPage from './pages/SchreibenTrainingPage';
import LessonStrategyPage from './components/lessons/LessonStrategyPage.jsx';
import LogamiLogoSpinner from './components/ui/LogamiLogoSpinner';
import AdminDashboard from './pages/AdminDashboard';
import ExamSlugRoute from './components/layout/ExamSlugRoute';
import RedirectToExamScoped from './components/layout/RedirectToExamScoped';
import {
  ExamSlugDetailPage,
  ExamSlugInstructionsPage,
  ExamSlugSimulatorPage,
  ExamSlugPaperPage,
  ExamSlugResultsPage, ExamSlugRetryPage,
  ExamSlugPracticePage,
  ExamSlugVocabularyPage,
  ExamSlugGrammarPage,
  ExamSlugRedemittelPage,
  ExamSlugExamSectionPractice,
  ExamSlugLesenTrainingPage,
  ExamSlugHorenTrainingPage,
  ExamSlugSchreibenTrainingPage,
  ExamSlugLessonsPage,
  ExamSlugLessonsLevelPage,
} from './components/exam/ExamSlugPages';
// --- Utils ---
import { generateDefaultAvatar } from './utils/avatar';
import subscriptionService from './services/subscriptionService';
import {
  trackPageView,
  trackPostLoginJourneyStep,
  getNextPostLoginStep,
  resetPostLoginJourney,
} from './utils/analytics';
import adminService from './services/adminService';
import { trackReturnMilestones } from './utils/conversionEvents';
import { GERMAN_MOCK_EXAM_SLUGS } from './data/mockExamCatalog';
import { hasGermanBlog } from './data/germanBlog';

const DEFAULT_LANG = 'en';
const EMAIL_VERIFIED_EVENT_KEY = 'logami.emailVerifiedAt';
const EMAIL_VERIFIED_CHANNEL = 'logami-auth-events';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function RedirectToDefaultLang() {
  const location = useLocation();
  const to = `/${DEFAULT_LANG}${location.pathname}${location.search}${location.hash}`;
  return <Navigate to={to} replace />;
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const pwa = usePWA(location.pathname);
  const [shouldAutoContinueGrammar, setShouldAutoContinueGrammar] = useState(false);
  const { i18n, t } = useTranslation();

  // Derive current :lang from URL for localized navigation (when under /:lang/...)
  const pathSegs = location.pathname.split('/').filter(Boolean);
  const currentLang = pathSegs[0] && isSupportedLang(pathSegs[0]) ? pathSegs[0] : DEFAULT_LANG;

  const localizedNavigate = (path, options) => {
    const p = typeof path === 'string' && path.startsWith('/') ? path : `/${path || ''}`;
    navigate(p === '/' ? `/${currentLang}` : `/${currentLang}${p}`, options);
  };

  const notifyEmailVerifiedAcrossTabs = () => {
    const payload = {
      type: 'email-verified',
      at: Date.now(),
    };

    try {
      localStorage.setItem(EMAIL_VERIFIED_EVENT_KEY, JSON.stringify(payload));
    } catch (error) {
      console.warn('Failed to write email verification event to localStorage:', error);
    }

    try {
      if (typeof BroadcastChannel !== 'undefined') {
        const channel = new BroadcastChannel(EMAIL_VERIFIED_CHANNEL);
        channel.postMessage(payload);
        channel.close();
      }
    } catch (error) {
      console.warn('BroadcastChannel is unavailable for email verification sync:', error);
    }
  };

  const getInitialExamData = () => {
    try {
      // Try to get user-specific exam data first
      const userId = auth.currentUser?.uid;
      if (userId) {
        const userExamData = localStorage.getItem(`selectedExamData_${userId}`);
        if (userExamData) {
          const parsed = JSON.parse(userExamData);
          console.log('📦 Loaded exam from user-specific localStorage:', parsed.examProvider, parsed.level);
          return parsed;
        }
      }

      // Fallback to session storage
      const savedData = sessionStorage.getItem('selectedExamData');
      if (savedData) {
        const parsed = JSON.parse(savedData);
        console.log('📦 Loaded exam from sessionStorage:', parsed.examProvider, parsed.level);
        return parsed;
      }

      // Fallback to general localStorage
      const generalData = localStorage.getItem('selectedExamData');
      if (generalData) {
        const parsed = JSON.parse(generalData);
        console.log('📦 Loaded exam from general localStorage:', parsed.examProvider, parsed.level);
        return parsed;
      }
      
      console.log('📦 No exam data found in storage');
    } catch (error) {
      console.error('Error loading exam data:', error);
    }
    return null;
  };
  const getInitialStudyPlan = () => {
    try {
      // Try to get user-specific study plan from localStorage
      const userId = auth.currentUser?.uid;
      if (userId) {
        const userPlan = localStorage.getItem(`personalizedPlan_${userId}`);
        if (userPlan) {
          console.log('Study plan loaded from localStorage');
          return JSON.parse(userPlan);
        }
      }

      // Fallback to general localStorage
      const savedPlan = localStorage.getItem('personalizedPlan');
      if (savedPlan) {
        console.log('Study plan loaded from localStorage (general)');
        return JSON.parse(savedPlan);
      }
    } catch (error) {
      console.error('Error loading study plan from localStorage:', error);
    }
    // Return null - Firebase loading will happen in useEffect
    return null;
  };

  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [avatarUrl, setAvatarUrl] = useState('');
  const [isLoadingAvatar, setIsLoadingAvatar] = useState(false);
  const [selectedExamData, setSelectedExamData] = useState(null); // Start with null, load from Firebase first
  // Distinguishes "no exam chosen" from "exam not fetched yet". Without it the
  // exam pages render NoExamSelected during the initial load, telling a user
  // with a perfectly good selection that they have none.
  const [isLoadingExamData, setIsLoadingExamData] = useState(true);
  const [personalizedPlan, setPersonalizedPlan] = useState(null);
  const [userProgress, setUserProgress] = useState(null);
  const [examResults, setExamResults] = useState(null);
  const [practiceSectionId, setPracticeSectionId] = useState(null);
  const [practiceFilter, setPracticeFilter] = useState('all');
  const [writingResultsData, setWritingResultsData] = useState(null);
  const [authStateResolved, setAuthStateResolved] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(null);

  // Dynamic page title based on route + page view + post-login journey tracking
  useEffect(() => {
    const pathWithoutLang = pathSegs.length > 1 ? '/' + pathSegs.slice(1).join('/') : '/';
    const getPageTitle = () => {
      const path = location.pathname;
      const searchParams = new URLSearchParams(location.search);
      const titleMap = {
        '/': t('home.title', { defaultValue: 'Home' }),
        '/dashboard': t('dashboard.title', { defaultValue: 'Dashboard' }),
        '/auth': t('auth.title', { defaultValue: 'Sign In' }),
        '/practice': t('practice.title', { defaultValue: 'Practice' }),
        '/practice/vocabulary': t('vocabulary.title', { defaultValue: 'Vocabulary' }),
        '/practice/grammar': t('grammar.title', { defaultValue: 'Grammar' }),
        '/practice/redemittel': t('redemittel.title', { defaultValue: 'Redemittel' }),
        '/lessons': t('lessons.title', { defaultValue: 'Lessons' }),
        '/exam': t('exam.title', { defaultValue: 'Exam' }),
        '/exam/instructions': t('exam.instructions.title', { defaultValue: 'Exam Instructions' }),
        '/exam/simulator': t('exam.simulator.title', { defaultValue: 'Exam Simulator' }),
        '/try': t('tryExam.pageTitle', { defaultValue: 'Free German Mock Exam. TELC, Goethe & ÖSD | Logami' }),
        '/exam/results': t('exam.results.title', { defaultValue: 'Exam Results' }),
        '/writing-lab': t('writingLab.title', { defaultValue: 'Writing Lab' }),
        '/study-plan': t('studyPlan.title', { defaultValue: 'Study Plan' }),
        '/study-plan/wizard': t('studyPlan.wizard.title', { defaultValue: 'Create Study Plan' }),
        '/settings': t('settings.title', { defaultValue: 'Settings' }),
        '/profile': t('profile.title', { defaultValue: 'Profile' }),
        '/subscription': t('subscription.title', { defaultValue: 'Subscription' }),
        '/pricing': t('pricing.title', { defaultValue: 'Pricing' }),
        '/resources': t('resources.title', { defaultValue: 'Resources' }),
        '/faq': t('faq.title', { defaultValue: 'FAQ' }),
        '/about': t('about.title', { defaultValue: 'About Us' }),
        '/contact': t('contact.title', { defaultValue: 'Contact' }),
        '/blog': t('blog.title', { defaultValue: 'Blog' }),
        '/checkout': t('checkout.title', { defaultValue: 'Checkout' }),
        '/admin': t('admin.title', { defaultValue: 'Admin Dashboard' }),
        '/party-games/top-10': t('partyGames.top10.metaTitle', { defaultValue: 'Top 10 Party Game' }),
      };
      if (pathWithoutLang.startsWith('/lessons/')) {
        const level = pathWithoutLang.split('/lessons/')[1]?.split('?')[0]?.toUpperCase() || '';
        return level ? `${level} ${t('grammar.title', { defaultValue: 'Grammar' })}` : t('lessons.title', { defaultValue: 'Lessons' });
      }
      if (pathWithoutLang.startsWith('/exam-structure/')) return t('exam.structure.title', { defaultValue: 'Exam Structure' });
      if (pathWithoutLang.startsWith('/lesson-strategy')) return t('lesson.strategy.title', { defaultValue: 'Lesson Strategy' });
      return titleMap[pathWithoutLang] || 'Logami';
    };
    const pageTitle = getPageTitle();
    const siteTitle = 'Pass TELC,Goethe & ÖSD Exams | Logami Exam Simulator';
    // Grammar and vocabulary pages set their own title (topic/category); don't overwrite
    const isGrammarOrVocabPractice = pathWithoutLang.includes('practice/grammar') || pathWithoutLang.includes('practice/vocabulary');
    if (!isGrammarOrVocabPractice) {
      if (pathWithoutLang === '/' || pageTitle === 'Logami') {
        document.title = siteTitle;
      } else {
        document.title = `${pageTitle} | Logami`;
      }
    }
    // SEO: per-page canonical URL so Google indexes the correct URL
    // Normalize: strip trailing slash (except bare /{lang}/ which is the homepage)
    const rawPath = location.pathname.replace(/\/+$/, '') || '/';
    // Blog posts are English-only content; non-English variants canonicalize to /en/
    // so Google indexes one authoritative URL instead of treating each /{lang}/ copy as duplicate.
    const canonSegs = rawPath.split('/').filter(Boolean);
    const isBlogPost = canonSegs.length === 3 && canonSegs[1] === 'blog';
    // Exception: a post translated into German (src/data/germanBlog.js) is its own page at
    // /de/blog/{slug}: self-canonical, and an en+de hreflang pair with the English post.
    const blogHasGerman = isBlogPost && hasGermanBlog(canonSegs[2]) && ['en', 'de'].includes(canonSegs[0]);
    const canonicalPath = (isBlogPost && canonSegs[0] !== 'en' && !blogHasGerman) ? `/en/blog/${canonSegs[2]}` : rawPath;
    const canonicalUrl = `https://logami.app${canonicalPath}`;
    let linkCanonical = document.querySelector('link[rel="canonical"]');
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute('href', canonicalUrl);

    // SEO: hreflang tags for multilingual support, tells Google which URL serves which language
    // Remove ALL alternate hreflang links (both prerendered ones and any previously injected by
    // this effect) before injecting the correct set. Using link[rel="alternate"][hreflang] catches
    // prerendered tags (no data-hreflang attr) and client-injected tags alike, preventing duplicates.
    const SUPPORTED_LANGS = ['en', 'de', 'fr', 'es', 'tr', 'ru'];
    const pathAfterLang = pathSegs.length > 1 ? '/' + pathSegs.slice(1).join('/') : '/';
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
    // Which languages a page ACTUALLY exists in. This must mirror the prerender list in
    // scripts/prerender.js — this effect strips every prerendered alternate and re-injects
    // its own, so a mismatch here silently overrides the build-time tags.
    //
    // Advertising a language we don't prerender points Google at the bare SPA shell
    // (200 OK, 0 visible characters). That is what put /de/mock-exams/osd-b1 into the
    // index at position 62.5. See doc/SEO_MOCK_EXAM_RANKING_DIAGNOSIS_2026-08.md §5.
    const langsForPath = (() => {
      // Exam pages with a German version (GERMAN_MOCK_EXAM_SLUGS) are en + de.
      const mockExamSlug = pathAfterLang.startsWith('/mock-exams/') ? pathAfterLang.split('/')[2] : null;
      if (pathAfterLang === '/mock-exams' || (mockExamSlug && GERMAN_MOCK_EXAM_SLUGS.includes(mockExamSlug))) {
        return ['en', 'de'];
      }
      // /try and /mock-exams[/*] are prerendered for English only.
      if (pathAfterLang === '/try' || pathAfterLang === '/mock-exams' || pathAfterLang.startsWith('/mock-exams/') || pathAfterLang === '/grammar' || pathAfterLang.startsWith('/grammar/')) {
        return ['en'];
      }
      // /telc-b1 and /telc-b2 landing pages have translated copy in en + de only.
      if (pathAfterLang === '/telc-b1' || pathAfterLang === '/telc-b2') {
        return ['en', 'de'];
      }
      return SUPPORTED_LANGS;
    })();
    // Blog posts get no hreflang at all: the article body is English-only, so every
    // /{lang}/blog/{slug} is a duplicate canonicalizing to /en/ (see canonicalPath
    // above) rather than a language alternate. hreflang alternates must be
    // self-canonical, so advertising them here contradicted our own canonical tag.
    if (!isBlogPost || blogHasGerman) {
      (blogHasGerman ? ['en', 'de'] : langsForPath).forEach((lang) => {
        const el = document.createElement('link');
        el.setAttribute('rel', 'alternate');
        el.setAttribute('hreflang', lang);
        el.setAttribute('href', `https://logami.app/${lang}${pathAfterLang}`);
        el.setAttribute('data-hreflang', lang);
        document.head.appendChild(el);
      });
      const elDefault = document.createElement('link');
      elDefault.setAttribute('rel', 'alternate');
      elDefault.setAttribute('hreflang', 'x-default');
      elDefault.setAttribute('href', `https://logami.app/en${pathAfterLang}`);
      elDefault.setAttribute('data-hreflang', 'x-default');
      document.head.appendChild(elDefault);
    }

    // SEO: keep <html lang> and og:locale in sync with the active language
    document.documentElement.lang = currentLang;
    const localeMap = { en: 'en_US', de: 'de_DE', fr: 'fr_FR', es: 'es_ES', tr: 'tr_TR', ru: 'ru_RU' };
    const ogLocale = document.querySelector('meta[property="og:locale"]');
    if (ogLocale) ogLocale.setAttribute('content', localeMap[currentLang] || 'en_US');

    const path = `${location.pathname}${location.search}`;
    trackPageView(path, document.title);
    if (isAuthenticated) {
      const step = getNextPostLoginStep();
      if (step != null) {
        trackPostLoginJourneyStep(path, document.title, step, isFirstTimeUser === true);
      }
    }
  }, [location.pathname, location.search, t, isAuthenticated, isFirstTimeUser]);

  useEffect(() => {
    const initializeLanguage = async () => {
      // Check localStorage first
      const storedLanguage = getStoredLanguage();

      if (storedLanguage) {
        await i18n.changeLanguage(storedLanguage);
      } else if (isFirstVisit()) {
        const detectedLang = detectUserLanguage();
        await i18n.changeLanguage(detectedLang);
        setLanguagePreference(detectedLang);
      }
    };

    initializeLanguage();
  }, [i18n]);


useEffect(() => {
  const handleResize = () => setIsMobile(window.innerWidth < 768);
  window.addEventListener('resize', handleResize);
  return () => window.removeEventListener('resize', handleResize);
}, []);

  // Listen for exam selection events from NoExamSelected component
  useEffect(() => {
    const handleExamSelected = async (event) => {
      const examData = event.detail;
      if (examData) {
        console.log('✅ Exam selected event received:', examData);
        setSelectedExamData(examData);
        // Save exam data (saveExamData is defined in the same component scope)
        const userId = auth.currentUser?.uid;
        if (userId && examData) {
          try {
            localStorage.setItem(`selectedExamData_${userId}`, JSON.stringify(examData));
            sessionStorage.setItem('selectedExamData', JSON.stringify(examData));
            localStorage.setItem('selectedExamData', JSON.stringify(examData));
            
            const saveResult = await userService.saveExamSelection(userId, examData);
            if (saveResult.success) {
              console.log('✅ Exam selection saved to Firebase');
            }
          } catch (error) {
            console.error('Error saving exam data:', error);
          }
        }
        // Reload to refresh all components with new exam data
        window.location.reload();
      }
    };

    window.addEventListener('examSelected', handleExamSelected);
    return () => window.removeEventListener('examSelected', handleExamSelected);
  }, []);

  // Load subscription status (same logic used by SubscriptionManagementPage).
  // Returns the status so callers can tell whether premium has landed yet.
  const refreshSubscriptionStatus = useCallback(async ({ showLoading = true } = {}) => {
    try {
      if (showLoading) setIsLoadingSubscription(true);

      if (!auth.currentUser) {
        setSubscriptionStatus(subscriptionService.getDefaultFreeStatus());
        return null;
      }

      const status = await subscriptionService.getSubscriptionStatus(auth.currentUser.uid);
      setSubscriptionStatus(status);
      return status;
    } catch (error) {
      console.error('Error loading subscription status:', error);
      setSubscriptionStatus(subscriptionService.getDefaultFreeStatus());
      return null;
    } finally {
      if (showLoading) setIsLoadingSubscription(false);
    }
  }, []);

  useEffect(() => {
    // Only check once auth is resolved (prevents flicker)
    if (!authStateResolved) return;
    refreshSubscriptionStatus();
  }, [authStateResolved, auth.currentUser?.uid, refreshSubscriptionStatus]);

  // Re-check when the user comes back to the tab. Covers a purchase completed
  // elsewhere and a plan that expired while the app sat open. The Lesen/Hören/
  // Schreiben pages each did this for themselves; doing it here covers the
  // dashboard, study plan, vocabulary, Redemittel and lessons too.
  useEffect(() => {
    if (!authStateResolved) return;
    const handleFocus = () => refreshSubscriptionStatus({ showLoading: false });
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [authStateResolved, refreshSubscriptionStatus]);

  const paymentJustSucceeded = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('payment') === 'success' || params.get('checkout') === 'success';
  }, [location.search]);

  // Checkout returns via client-side navigation, so nothing remounts and the
  // status fetched at sign-in stays 'free'. Without this the buyer lands on the
  // dashboard still paywalled — and gets upsell modals aimed at them moments
  // after paying. Poll rather than fetch once: access is granted by the Stripe
  // webhook, which can land a second or two after the redirect.
  useEffect(() => {
    if (!authStateResolved || !paymentJustSucceeded) return;
    let cancelled = false;

    (async () => {
      for (let attempt = 0; attempt < 6 && !cancelled; attempt += 1) {
        const status = await refreshSubscriptionStatus({ showLoading: attempt === 0 });
        if (cancelled || (status?.isActive && !status.isFree)) return;
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    })();

    return () => { cancelled = true; };
  }, [authStateResolved, paymentJustSucceeded, refreshSubscriptionStatus]);

  // Check if current user is admin
  useEffect(() => {
    if (auth.currentUser) {
      const adminStatus = adminService.isAdmin();
      setIsAdmin(adminStatus);
      console.log('Admin status:', adminStatus);
    } else {
      setIsAdmin(false);
    }
  }, [auth.currentUser?.uid]);
  const handleViewWritingResults = (writingData) => {
  console.log('📝 Navigating to writing results with data:', writingData);
  setWritingResultsData(writingData);
  localizedNavigate('/exam/writing-results');
};
  // ADDED: Load exam data and study plan when user changes
  useEffect(() => {
    const loadUserData = async () => {
      if (!auth.currentUser?.uid) return;

      // PRIORITY: Load exam data from Firebase FIRST
      try {
        const profile = await userService.getUserProfile(auth.currentUser.uid);
        if (profile) {
          // Check examSelection field first (new structure)
          if (profile.examSelection && profile.examSelection.examProvider && profile.examSelection.level) {
            console.log('🔥 PRIORITY: Loading exam from Firebase (examSelection):', profile.examSelection.examProvider, profile.examSelection.level);
            try {
              const userExamData = await examService.loadExam(
                profile.examSelection.examProvider, 
                profile.examSelection.level
              );
              console.log('✅ Exam loaded from Firebase:', userExamData.examName);
              setSelectedExamData(userExamData);
              await saveExamData(userExamData);
              return; // Exit early, Firebase data loaded
            } catch (examError) {
              console.error('❌ Error loading exam from Firebase:', examError);
            }
          }
          // Check selectedExamProvider/selectedExamLevel (old structure)
          else if (profile.selectedExamProvider && profile.selectedExamLevel) {
            console.log('🔥 PRIORITY: Loading exam from Firebase (selectedExamProvider/selectedExamLevel):', profile.selectedExamProvider, profile.selectedExamLevel);
            try {
              const userExamData = await examService.loadExam(
                profile.selectedExamProvider, 
                profile.selectedExamLevel
              );
              console.log('✅ Exam loaded from Firebase:', userExamData.examName);
              setSelectedExamData(userExamData);
              await saveExamData(userExamData);
              return; // Exit early, Firebase data loaded
            } catch (examError) {
              console.error('❌ Error loading exam from Firebase:', examError);
            }
          }

          // Both branches above fall through to here when the paper itself could
          // not be fetched. Leaving selectedExamData null makes every exam page
          // render "No Exam Selected", which reads as a broken account even
          // though the selection is sitting right there in the profile.
          // Provider + level is all the exam UI needs to render and, for a free
          // user, show the paywall. Mirrors the fallback the onboarding path in
          // handleOnboardingCompleteFromPage has always had.
          const fallbackProvider = profile.examSelection?.examProvider || profile.selectedExamProvider;
          const fallbackLevel = profile.examSelection?.level || profile.selectedExamLevel;
          if (fallbackProvider && fallbackLevel) {
            console.warn('⚠️ Paper fetch failed; falling back to provider/level only:', fallbackProvider, fallbackLevel);
            setSelectedExamData({
              examProvider: fallbackProvider,
              level: fallbackLevel,
              examName: profile.examSelection?.examName || `${fallbackProvider} ${fallbackLevel}`,
            });
            return;
          }
        }
      } catch (firebaseError) {
        console.error('Error loading from Firebase:', firebaseError);
      }

      // FALLBACK: Only use localStorage if Firebase didn't have exam data
      if (!selectedExamData) {
        const examData = getInitialExamData();
        if (examData) {
          console.log('📦 FALLBACK: Loading exam from localStorage:', examData.examProvider, examData.level);
          setSelectedExamData(examData);
        }
      }

      // Load study plan - try localStorage first, then Firebase
      let studyPlan = getInitialStudyPlan();
      
      if (studyPlan && !personalizedPlan) {
        console.log('Study plan loaded from localStorage');
        setPersonalizedPlan(studyPlan);
      } else if (!studyPlan && !personalizedPlan) {
        // If not in localStorage, try Firebase
        console.log('Study plan not in localStorage, checking Firebase...');
        try {
          const firebaseStudyPlan = await userService.getStudyPlan(auth.currentUser.uid);
          if (firebaseStudyPlan) {
            console.log('Study plan loaded from Firebase');
            setPersonalizedPlan(firebaseStudyPlan);
            // Save to localStorage for faster access next time
            localStorage.setItem(`personalizedPlan_${auth.currentUser.uid}`, JSON.stringify(firebaseStudyPlan));
          } else {
            console.log('No study plan found in Firebase');
          }
        } catch (error) {
          console.error('Error loading study plan from Firebase:', error);
        }
      }
    };

    loadUserData().finally(() => setIsLoadingExamData(false));
  }, [auth.currentUser?.uid]);
//   useEffect(() => {
//   // Register service worker for PWA functionality
//   registerServiceWorker();
//
//   // Show install prompt after delay
//   showInstallPrompt();
// }, []);
  // IMPROVED: Save exam data with user-specific keys AND to Firebase
  const saveExamData = async (examData) => {
    const userId = auth.currentUser?.uid;
    if (!userId) {
      console.error('Cannot save exam data: user not logged in');
      return;
    }
    if (!examData) {
      console.error('Cannot save exam data: exam data is missing');
      return;
    }

    try {
      // Save to localStorage for immediate access
      localStorage.setItem(`selectedExamData_${userId}`, JSON.stringify(examData));
      // Also save to session storage for immediate access
      sessionStorage.setItem('selectedExamData', JSON.stringify(examData));
      // Fallback general storage
      localStorage.setItem('selectedExamData', JSON.stringify(examData));
      console.log('✅ Exam data saved to localStorage for user:', userId, examData.examName || examData.examProvider);
      
      // Also save to Firebase for persistence across devices
      const saveResult = await userService.saveExamSelection(userId, examData);
      if (saveResult.success) {
        console.log('✅ Exam selection saved to Firebase successfully for user:', userId);
      } else {
        console.error('❌ Failed to save exam selection to Firebase:', saveResult.error);
        console.warn('Exam data saved locally but not to Firebase. It may not persist across devices.');
      }
    } catch (error) {
      console.error('❌ Error saving exam data:', error);
      console.warn('Exam data saved locally but Firebase save failed:', error.message);
    }
  };

  // IMPROVED: Save study plan with user-specific keys AND to Firebase
  const saveStudyPlan = async (plan) => {
    const userId = auth.currentUser?.uid;
    if (!userId) {
      console.error('Cannot save study plan: user not logged in');
      return;
    }
    if (!plan) {
      console.error('Cannot save study plan: plan data is missing');
      return;
    }

    try {
      // Save to localStorage for immediate access
      localStorage.setItem(`personalizedPlan_${userId}`, JSON.stringify(plan));
      localStorage.setItem('personalizedPlan', JSON.stringify(plan));
      console.log('✅ Study plan saved to localStorage for user:', userId);
      
      // Also save to Firebase for persistence across devices
      const saveResult = await userService.saveStudyPlan(userId, plan);
      if (saveResult.success) {
        console.log('✅ Study plan saved to Firebase successfully for user:', userId);
      } else {
        console.error('❌ Failed to save study plan to Firebase:', saveResult.error);
        // Show user-friendly error message
        console.warn('Study plan saved locally but not to Firebase. It may not persist across devices.');
      }
    } catch (error) {
      console.error('❌ Error saving study plan:', error);
      // localStorage save succeeded, so don't throw - but log the error
      console.warn('Study plan saved locally but Firebase save failed:', error.message);
    }
  };

  // Handle email verification and password reset from URL parameters
  const handleAuthActions = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const mode = urlParams.get('mode');
    const oobCode = urlParams.get('oobCode');

    console.log('Checking URL for auth actions:', { mode, oobCode: oobCode ? 'present' : 'missing', pathname: location.pathname });

    // Handle password reset - redirect to /auth with parameters
    if (mode === 'resetPassword' && oobCode) {
      console.log('Password reset detected, redirecting to /auth');
      // Preserve all URL parameters when redirecting to /auth
      const continueUrl = urlParams.get('continueUrl');
      const apiKey = urlParams.get('apiKey');
      const lang = urlParams.get('lang');
      
      let redirectUrl = `/${currentLang}/auth?mode=resetPassword&oobCode=${encodeURIComponent(oobCode)}`;
      if (apiKey) redirectUrl += `&apiKey=${encodeURIComponent(apiKey)}`;
      if (continueUrl) redirectUrl += `&continueUrl=${encodeURIComponent(continueUrl)}`;
      if (lang) redirectUrl += `&lang=${encodeURIComponent(lang)}`;
      
      navigate(redirectUrl);
      return true;
    }

    // Handle email verification
    if (mode === 'verifyEmail' && oobCode) {
      try {
        console.log('Applying email verification code...');
        await applyActionCode(auth, oobCode);
        console.log('Email verified successfully!');

        // Clear URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);

        // Force reload current user to get updated verification status
        if (auth.currentUser) {
          await auth.currentUser.reload();
          console.log('User reloaded, verified status:', auth.currentUser.emailVerified);
        }

        // Notify other open app tabs so they can transition out of the waiting screen.
        notifyEmailVerifiedAcrossTabs();

        // Redirect to verification success page
        navigate(`/${currentLang}/verified`);
        return true;
      } catch (error) {
        console.error('Email verification failed:', error);
        // Redirect to auth page with error
        navigate(`/${currentLang}/auth?error=verification_failed`);
        return false;
      }
    }
    return false;
  };

  // Handle email verification and password reset on component mount
  useEffect(() => {
    handleAuthActions();
  }, [location.search]); // Re-run when URL parameters change

    useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      console.log('Auth state changed:', currentUser?.uid);

      if (currentUser) {
        console.log('User authenticated:', currentUser.uid, 'verified:', currentUser.emailVerified);
        resetPostLoginJourney();
        setIsAuthenticated(true);
        trackReturnMilestones(currentUser);

        // Start tracking total website time
        try {
          websiteTimeTracker.startTracking();
          console.log('Website time tracking started');
        } catch (error) {
          console.error('Error starting website time tracking:', error);
        }
        
       try {
  await logDailyLogin();
  console.log('Daily login and streak check processed.');
} catch (error) {
  console.error('Error during daily login process:', error);
}

// Check if this is first verified login - send welcome email
try {
  const userRef = doc(db, 'users', currentUser.uid);
  const userDoc = await getDoc(userRef);

  if (userDoc.exists()) {
    const userData = userDoc.data();

    // Send welcome email if not sent yet
   if (!userData.welcomeEmailSent) {
  console.log('First verified login - sending welcome email. The backend will mark it as sent.');
  try {
    // The backend function now handles sending the email AND updating the user profile.
    await sendWelcomeEmail(currentUser);
  } catch (emailError) {
    console.error('Failed to send welcome email:', emailError);
    // Don't block login if email fails
  }
}
  }
} catch (error) {
  console.error('Error checking welcome email status:', error);
}

try {
          // Try to get user data from Firestore first
          const userRef = doc(db, 'users', currentUser.uid);
          const userDoc = await getDoc(userRef);

          let photoURL = null;

          if (userDoc.exists()) {
            const userData = userDoc.data();
            // Get photoURL from Firestore document
            photoURL = userData.photoURL || userData.avatarUrl;
            console.log('Found photoURL in Firestore:', photoURL);
            // For first-time user journey analytics (e.g. where they land after sign-in)
            setIsFirstTimeUser(!userData.welcomeEmailSent);
          } else {
            setIsFirstTimeUser(true);
          }

          // Fallback to Firebase Auth photoURL
          if (!photoURL && currentUser.photoURL) {
            photoURL = currentUser.photoURL;
            console.log('Using Firebase Auth photoURL:', photoURL);
          }

          // Set avatar URL or generate default
          if (photoURL) {
            setAvatarUrl(photoURL);
          } else {
            const defaultAvatar = generateDefaultAvatar(currentUser.uid);
            setAvatarUrl(defaultAvatar);
            console.log('Generated default avatar');
          }

          // Get user progress
          // Get user progress
          const progress = await userService.getUserProgress(currentUser.uid);
          setUser({ ...currentUser, photoURL, ...progress });
          setUserProgress(progress);

          // PRIORITY 1: Load exam data from Firebase FIRST (source of truth)
          const profile = await userService.getUserProfile(currentUser.uid);
          let examDataFromFirebase = null;

          if (profile) {
            setUserProgress(profile);

            // Apply saved language preference
            const savedLanguage = profile.preferences?.language;
            if (savedLanguage && savedLanguage !== i18n.language) {
                await i18n.changeLanguage(savedLanguage);
                console.log(`Loaded saved language: ${savedLanguage}`);
            }
            
            // Check examSelection field first (new structure)
            if (profile.examSelection && profile.examSelection.examProvider && profile.examSelection.level) {
              console.log('🔥 PRIORITY: Loading exam from Firebase (examSelection):', profile.examSelection.examProvider, profile.examSelection.level);
              try {
                const userExamData = await examService.loadExam(
                  profile.examSelection.examProvider, 
                  profile.examSelection.level
                );
                console.log('✅ Exam loaded from Firebase (examSelection):', userExamData.examName);
                examDataFromFirebase = userExamData;
                setSelectedExamData(userExamData);
                await saveExamData(userExamData);
              } catch (examError) {
                console.error('❌ Error loading exam from Firebase (examSelection):', examError);
              }
            }
            // Check selectedExamProvider/selectedExamLevel (old structure) if examSelection didn't work
            else if (profile.selectedExamProvider && profile.selectedExamLevel) {
              console.log('🔥 PRIORITY: Loading exam from Firebase (selectedExamProvider/selectedExamLevel):', profile.selectedExamProvider, profile.selectedExamLevel);
              try {
                const userExamData = await examService.loadExam(
                  profile.selectedExamProvider, 
                  profile.selectedExamLevel
                );
                console.log('✅ Exam loaded from Firebase (selectedExamProvider/selectedExamLevel):', userExamData.examName);
                examDataFromFirebase = userExamData;
                setSelectedExamData(userExamData);
                await saveExamData(userExamData);
              } catch (examError) {
                console.error('❌ Error loading exam from Firebase (selectedExamProvider/selectedExamLevel):', examError);
              }
            }
          }

          // PRIORITY 2: Fallback to localStorage ONLY if Firebase didn't have exam data
          if (!examDataFromFirebase) {
            const examDataFromLocalStorage = getInitialExamData();
            if (examDataFromLocalStorage) {
              console.log('📦 FALLBACK: Loading exam from localStorage:', examDataFromLocalStorage.examProvider, examDataFromLocalStorage.level);
              setSelectedExamData(examDataFromLocalStorage);
            } else {
              console.log('📦 No exam data found in Firebase or localStorage');
            }
          }

          // Load study plan - try localStorage first, then Firebase
          const studyPlan = getInitialStudyPlan();
          if (studyPlan) {
            console.log('Restoring study plan from localStorage');
            setPersonalizedPlan(studyPlan);
          } else {
            // Load study plan from Firebase if not in localStorage
            console.log('Checking Firebase for study plan...');
            try {
              const firebaseStudyPlan = await userService.getStudyPlan(currentUser.uid);
              if (firebaseStudyPlan) {
                console.log('Study plan loaded from Firebase');
                setPersonalizedPlan(firebaseStudyPlan);
                // Also save to localStorage for faster access next time
                localStorage.setItem(`personalizedPlan_${currentUser.uid}`, JSON.stringify(firebaseStudyPlan));
              }
            } catch (planError) {
              console.error('Error loading study plan from Firebase:', planError);
            }
          }
          // Handle verification success from URL parameter
          const urlParams = new URLSearchParams(window.location.search);
          if (urlParams.get('verified') === 'true') {
            console.log('User just verified email from URL parameter');
            window.history.replaceState({}, document.title, window.location.pathname);
            notifyEmailVerifiedAcrossTabs();
            localizedNavigate('/verified');
          }
        } catch (error) {
          console.error('Error loading user data:', error);
          // Still set user even if Firestore fails
          setUser(currentUser);
          setAvatarUrl(currentUser.photoURL || generateDefaultAvatar(currentUser.uid));
        }
      } else {
        console.log('No user found');
        websiteTimeTracker.stopTracking();
        resetPostLoginJourney();
        setIsAuthenticated(false);
        setIsFirstTimeUser(null);
        setUser(null);
        setUserProgress(null);
        setSelectedExamData(null);
        setPersonalizedPlan(null);
        setAvatarUrl(null);
      }
      setAuthStateResolved(true);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [navigate],[i18n]);

  const navigateTo = (path) => {
    localizedNavigate(path);
    window.scrollTo(0, 0);
  };

  // Login without blocking on email verification.
  // Email verification is required at upgrade checkout instead.
  const handleLogin = async (email, password) => {
    console.log('Login attempt for:', email);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log('User authenticated. Verification will be enforced at checkout upgrade.');
      return userCredential;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  // Simple registration handler - verification is now handled by AuthPage
  const handleRegister = async (email, password) => {
    console.log('Registration attempt for:', email);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      console.log('Registration successful:', userCredential.user);
      return userCredential;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  const handleLogout = async () => {
    console.log('Logging out user');
    const userId = auth.currentUser?.uid;

    await signOut(auth);

    // Clear user-specific data but keep general fallbacks
    if (userId) {
      localStorage.removeItem(`selectedExamData_${userId}`);
      localStorage.removeItem(`personalizedPlan_${userId}`);
    }
    sessionStorage.removeItem('selectedExamData');
    // The unfinished-guest-exam marker is device-wide, so it would otherwise
    // follow this device into the next account signed in here. Same for any
    // leftover anonymous exam snapshot: the next account must not be able to
    // adopt this session's answers.
    clearGuestExamPending();
    clearAnonExamSessions();

    setSelectedExamData(null);
    setPersonalizedPlan(null);

    // Full redirect to /auth with reload so next Google sign-in works (clears stale redirect state)
    window.location.href = `/${currentLang}/auth`;
  };

  // Helper function to clear all exam data (useful for debugging)
  const clearAllExamData = () => {
    const userId = auth.currentUser?.uid;
    
    // Clear user-specific storage
    if (userId) {
      localStorage.removeItem(`selectedExamData_${userId}`);
      console.log('🗑️ Cleared user-specific exam data');
    }
    
    // Clear session storage
    sessionStorage.removeItem('selectedExamData');
    console.log('🗑️ Cleared sessionStorage exam data');
    
    // Clear general localStorage
    localStorage.removeItem('selectedExamData');
    console.log('🗑️ Cleared general localStorage exam data');
    
    // Clear state
    setSelectedExamData(null);
    console.log('🗑️ Cleared exam data from state');
    
    // Note: This doesn't clear Firebase profile data - that needs to be done separately
    console.log('⚠️ Note: Firebase profile exam data still exists. Use ProfilePage to clear it.');
  };
  
  // Expose clearAllExamData to window for debugging (can be called from browser console)
  if (typeof window !== 'undefined') {
    window.clearAllExamData = clearAllExamData;
  }

  const handlePlanGenerated = async (plan) => {
    console.log('Plan generated with data:', plan);
    try {
      // Plan is already generated by StudyPlanWizard, just save it
      // Always save the plan regardless of timelineAlert - it's a non-blocking warning
      setPersonalizedPlan(plan);
      await saveStudyPlan(plan); // Save to both localStorage and Firebase
      
      // Also save exam data if available
      if (plan.targetExam && plan.targetLevel) {
        const examData = {
          examProvider: plan.targetExam,
          level: plan.targetLevel
        };
        if (auth.currentUser?.uid) {
          await saveExamData(examData);
        } else {
          sessionStorage.setItem('selectedExamData', JSON.stringify(examData));
          setSelectedExamData(examData);
        }
      }
      
      // Redirect to study plan page with timelineAlert in router state (if present)
      localizedNavigate('/study-plan', {
        state: plan.timelineAlert ? { alert: plan.timelineAlert } : {}
      });
    } catch (error) {
      console.error('Error saving study plan:', error);
      // Fallback: still save the plan
      setPersonalizedPlan(plan);
      await saveStudyPlan(plan); // Save to both localStorage and Firebase
      localizedNavigate('/study-plan', {
        state: plan.timelineAlert ? { alert: plan.timelineAlert } : {}
      });
    }
  };

  /** Called by OnboardingPage after generating plan: save plan + exam state; page handles redirect to dashboard. */
  const handleOnboardingCompleteFromPage = async (plan) => {
    if (!plan) return;
    setPersonalizedPlan(plan);
    await saveStudyPlan(plan);
    let provider = (plan.targetExam || plan.examProvider || '').toString().toLowerCase();
    if (provider === 'ösd') provider = 'osd';
    const level = (plan.targetLevel || plan.level || 'B2').toString().toUpperCase();
    if (provider && level) {
      try {
        const examData = await examService.loadExam(provider, level);
        setSelectedExamData(examData);
        if (auth.currentUser?.uid) await saveExamData(examData);
      } catch (e) {
        console.error('Error loading exam after onboarding:', e);
        setSelectedExamData({ examProvider: provider, level, examName: `${provider} ${level}` });
      }
    }
  };

  const handlePlanVariationChange = async (newIntensity) => {
    if (personalizedPlan) {
      const updatedPlan = { ...personalizedPlan, intensity: newIntensity };
      
      // Update state first for immediate UI update
      setPersonalizedPlan(updatedPlan);
      
      // Save to localStorage and Firebase
      await saveStudyPlan(updatedPlan);
      
      // Also update just the intensity in Firebase for efficiency
      const userId = auth.currentUser?.uid;
      if (userId) {
        try {
          await userService.updateStudyPlanSettings(userId, { intensity: newIntensity });
        } catch (error) {
          console.error('Error updating intensity in Firebase:', error);
        }
      }
    }
  };

  // IMPROVED: Enhanced exam selection with better persistence
  // Now includes admin check - only admin can change exam after initial selection
  const handleExamSelect = async (provider, level, forceChange = false) => {
    console.log('Loading exam:', provider, level);
    
    // Check if user already has an exam selected and is not admin
    if (selectedExamData && !isAdmin && !forceChange) {
      console.log('User already has exam selected and is not admin. Exam change blocked.');
      // Navigate to dashboard with existing exam
      localizedNavigate('/dashboard');
      return;
    }
    
    setIsLoading(true);

    try {
      const examData = await examService.loadExam(provider, level);
      console.log('Exam loaded successfully:', examData.examName);

      setSelectedExamData(examData);
      await saveExamData(examData);

      // Navigate to dashboard
      localizedNavigate('/dashboard');
    } catch (error) {
      console.error('Error loading exam:', error);
      // Handle error - maybe show a notification
    } finally {
      setIsLoading(false);
    }
  };

 const handleStartMockExamVariant = async (variantFile) => {
  console.log(`Attempting to start exam variant with file: ${variantFile}`);
  setIsLoading(true);
  try {
    if (!selectedExamData) throw new Error("No base exam selected.");

    const { examProvider, level } = selectedExamData;
    const providerKey = examProvider.toLowerCase();
    const levelKey = level.toUpperCase();

    const allExams = examService.getAvailableExams();
    const availableVariants = allExams[providerKey]?.[levelKey] || [];

    const variantConfig = availableVariants.find(v => v.file === variantFile);

    if (!variantConfig) {
      // Fallback to navigate if a default/non-variant exam is started
      console.log('No specific variant file found, starting default exam flow.');
      localizedNavigate('/exam/instructions');
      return;
    }

    // Prevent loading "coming soon" exams
    if (variantConfig.comingSoon) {
      console.log('This exam variant is coming soon and cannot be started yet.');
      return;
    }

    // Use the unique variant ID to load the exam
    const examData = await examService.loadExam(examProvider, level, variantConfig.id);

    console.log('Variant exam loaded successfully:', examData.examName);
    setSelectedExamData(examData);
    await saveExamData(examData);
    localizedNavigate('/exam/instructions');

  } catch (error) {
    console.error('Error loading exam variant:', error);
  } finally {
    setIsLoading(false);
  }
};

  const handleStartPractice = (sectionId, filter) => {
    // Handle lessons from study plan
    if (sectionId === 'lessons' || sectionId === 'exam_lessons') {
      localizedNavigate('/lessons');
      return;
    }

    // Handle vocabulary and grammar from study plan
    if (sectionId === 'vocabulary') {
      localizedNavigate('/practice/vocabulary');
      return;
    }

    if (sectionId === 'grammar') {
      localizedNavigate('/practice/grammar');
      return;
    }

    if (sectionId === 'grammar_lessons') {
      localizedNavigate('/study/grammar');
      return;
    }

    if (sectionId === 'grammar_exercises') {
      localizedNavigate('/practice/grammar');
      return;
    }

    // Handle other study plan activities
    if (sectionId === 'reading') {
      setPracticeSectionId('reading');
      setPracticeFilter('reading');
      localizedNavigate('/practice');
      return;
    }

    if (sectionId === 'writing') {
      localizedNavigate('/practice/writing');
      return;
    }

    if (sectionId === 'listening') {
      setPracticeSectionId('listening');
      setPracticeFilter('listening');
      localizedNavigate('/practice');
      return;
    }

    if (sectionId === 'speaking') {
      setPracticeSectionId('speaking');
      setPracticeFilter('speaking');
      localizedNavigate('/practice');
      return;
    }

    // Handle regular exam practice (existing functionality)
    setPracticeSectionId(sectionId);
    setPracticeFilter(filter || 'all');
    localizedNavigate('/practice');
  };

  // Exam completion handler - logging now handled by ExamResultsPage
  // timeSpent (seconds) and sectionTimes from ExamSimulatorPage are included so they are saved
  const handleFinishExam = async (finalAnswers, flaggedQuestions, timeSpent = 0, sectionTimes = {}) => {
    // The exam is done: retire the dashboard "Continue your exam" banner. The
    // banner only appears for the user's own exam, so finishing here finishes
    // the paper it points at. (TryExamPage does the same for the guest flow.)
    clearGuestExamPending();
    const results = examService.calculateFinalScore(selectedExamData, finalAnswers);

    console.log('🎯 INITIAL EXAM RESULTS (before saving):', {
        overallScore: results.overallScore,
        maxScore: results.maxScore,
        provider: results.provider,
        sectionScores: results.sectionScores,
        hasSpeakingSection: results.hasSpeakingSection,
        timeSpent,
        sectionTimes
    });

    setExamResults({
        ...results,
        userAnswers: finalAnswers,
        flaggedQuestions,
        timeSpent: timeSpent || results.timeSpent || 0,
        sectionTimes: sectionTimes && Object.keys(sectionTimes).length > 0 ? sectionTimes : (results.sectionTimes || {})
    });

    localizedNavigate('/exam/results');
};

  const handleAvatarUpdate = async (file) => {
    if (!user || !file) return;

    setIsLoadingAvatar(true);
    try {
      console.log('Starting avatar upload for user:', user.uid);

      // Upload to Firebase Storage and update Firestore
      const url = await storageService.uploadProfilePhoto(user.uid, file);
      console.log('Avatar uploaded successfully:', url);

      // Update Firebase Auth profile
      await updateProfile(auth.currentUser, { photoURL: url });
      console.log('Firebase Auth profile updated');

      // Update local state
      setAvatarUrl(url);
      setUser(prevUser => ({ ...prevUser, photoURL: url }));

      // Force a small delay to ensure the image is cached
      await new Promise(resolve => setTimeout(resolve, 100));

      console.log('Avatar update complete');
      return url;
    } catch (error) {
      console.error('Error updating avatar:', error);
      throw error;
    } finally {
      setIsLoadingAvatar(false);
    }
  };
  // During react-snap pre-rendering, Firebase is blocked so auth never resolves.
  // Skip the loading gate in headless Chrome so public pages render their full content.
  const isPrerendering = typeof navigator !== 'undefined' && navigator.userAgent.includes('HeadlessChrome');
  if (!isPrerendering && (isLoading || !authStateResolved)) {
  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="flex items-center justify-center h-20 w-20 rounded-2xl bg-white shadow-md ring-1 ring-gray-100">
        <LogamiLogoSpinner size="hero" decorative={false} alt="Logami loading" />
      </div>
    </div>
  );
}

return (
  <>
    <ScrollToTop />
    <NetworkStatus isOnline={pwa.isOnline} />
    <ErrorBoundary>
      <Routes>
        {/* Root: redirect to /:lang/ for SEO and consistent language-in-URL */}
        <Route path="/" element={<Navigate to={`/${getStoredLanguage() || detectUserLanguage() || DEFAULT_LANG}`} replace />} />

        {/* All app routes under /:lang (e.g. /en/dashboard, /de/practice) */}
        <Route path="/:lang" element={<LangRoute />}>
          <Route index element={<HomePageB user={user} isAuthenticated={isAuthenticated} />} />
          {/* Founder portfolio: the real page is the static /emir file; this route only
              catches browsers whose older service worker served the app shell for /emir. */}
          <Route path="emir" element={<React.Suspense fallback={null}><FounderPortfolioPage /></React.Suspense>} />
          {/* Public Routes */}
          <Route element={
            <PublicLayout
              user={user}
              avatarUrl={avatarUrl}
              onLogout={handleLogout}
              authStateResolved={authStateResolved}
            />
          }>
            <Route path="faq" element={<FaqPage />} />
            <Route path="about" element={<AboutUsPage />} />
            <Route path="pricing" element={<PricingPage />} />
            <Route path="contact" element={<ContactPage />} />
            <Route path="blog" element={<BlogPage />} />
            <Route path="blog/:slug" element={<BlogPostPage />} />
            {/* Public, indexable exam pages. Distinct from the auth-gated
                /exams/:examSlug simulator routes below: these are marketing/SEO
                pages describing format, timing and scoring, open to crawlers. */}
            <Route path="mock-exams" element={<MockExamIndexPage />} />
            <Route path="mock-exams/:examSlug" element={<MockExamLandingPage />} />
            {/* Public grammar library. Distinct from the authenticated trainer at
                study/grammar and practice/grammar: these render the pillar's
                explanation only and never its exercises. English-only, like the
                rest of the SEO cluster. */}
            <Route path="grammar" element={<GrammarHubPage />} />
            <Route path="grammar/:level" element={<GrammarLevelPage />} />
            <Route path="grammar/:level/:slug" element={<GrammarTopicPage />} />
            {PartyGamePage && (
              <Route path="party-games/top-10" element={<React.Suspense fallback={null}><PartyGamePage /></React.Suspense>} />
            )}
          </Route>
          {/* Win-back survey: personal link from Anna's email, no login (the token is the credential). */}
          <Route path="survey/:token" element={<React.Suspense fallback={null}><SurveyPage /></React.Suspense>} />
          {/* TELC B1 Google Ads campaign landing page */}
          <Route path="telc-b1" element={<TelcB1LandingPage user={user} isAuthenticated={isAuthenticated} />} />
          {/* TELC B2 dedicated landing page */}
          <Route path="telc-b2" element={<TelcB2LandingPage user={user} isAuthenticated={isAuthenticated} />} />
          {PaperExamPreviewPage && (
            <Route path="dev/paper" element={<React.Suspense fallback={null}><PaperExamPreviewPage /></React.Suspense>} />
          )}
          {/* Public guest mock exam: first section free, signup gate to continue */}
          <Route path="try" element={<TryExamPage user={user} isAuthenticated={isAuthenticated} />} />
          {PartyGamePage && (
            <Route path="party-game" element={<Navigate to={`/${currentLang}/party-games/top-10`} replace />} />
          )}
          {/* Legal Pages */}
          <Route path="terms" element={<TermsOfServicePage />} />
          <Route path="privacy" element={<PrivacyPolicyPage />} />
          <Route path="cookies" element={<CookiePolicyPage />} />
          {/* Auth Routes */}
          <Route path="verified" element={<VerifiedPage />} />
          <Route path="auth" element={!isAuthenticated ? <AuthPage onLogin={handleLogin} onRegister={handleRegister} onPlanGenerated={handlePlanGenerated} /> : <PostAuthRedirect currentLang={currentLang} />} />
          <Route path="checkout" element={isAuthenticated ? <CheckoutPage /> : <Navigate to={`/${currentLang}/auth?redirect=/checkout`} replace />} />
          <Route path="checkout/success" element={isAuthenticated ? <CheckoutPage /> : <Navigate to={`/${currentLang}/auth`} replace />} />

          {/* Protected Routes: onboarding has no guard; all other post-signup routes require onboarding + plan */}
          <Route element={isAuthenticated ? <AppLayout user={user} avatarUrl={avatarUrl} userProgress={userProgress} selectedExamData={selectedExamData} personalizedPlan={personalizedPlan} onLogout={handleLogout} isInstallable={pwa.isInstallable} onInstallApp={pwa.installApp} updateAvailable={pwa.updateAvailable} onUpdateApp={pwa.updateApp} onShareApp={pwa.shareApp} isStandalone={pwa.isStandalone} isAdmin={isAdmin} subscriptionStatus={subscriptionStatus} isLoadingSubscription={isLoadingSubscription} /> : <SignInFirst currentLang={currentLang} />}>
            <Route element={<BlockOnboardingRerun user={user} subscriptionStatus={subscriptionStatus} isLoadingSubscription={isLoadingSubscription} isAdmin={isAdmin} />}>
              <Route path="onboarding" element={
                <OnboardingPage
                  user={user}
                  onOnboardingComplete={handleOnboardingCompleteFromPage}
                  currentLang={currentLang}
                />
              } />
            </Route>
            <Route element={<RequireOnboarding user={user} personalizedPlan={personalizedPlan} />}>
              <Route path="dashboard" element={
                <CompactDashboard
                  user={user}
                  userProgress={userProgress}
                  personalizedPlan={personalizedPlan}
                  onExamSelectionComplete={(examData) => {
                    setSelectedExamData(examData);
                    saveExamData(examData);
                  }}
                />
              } />
              <Route path="admin" element={<AdminDashboard />} />
            <Route path="lessons" element={<RedirectToExamScoped selectedExamData={selectedExamData} isLoadingExamData={isLoadingExamData} toPath="lessons"><StudyPlanPage examProvider={selectedExamData?.examProvider} examLevel={selectedExamData?.level} /></RedirectToExamScoped>} />
            <Route path="exam-structure/:provider/:level" element={<ExamStructurePage />} />
            <Route path="lesson-strategy" element={<LessonStrategyPage />} />
            <Route path="exam/simulator" element={<RedirectToExamScoped selectedExamData={selectedExamData} isLoadingExamData={isLoadingExamData} toPath="simulator"><ExamSimulatorPage user={user} examData={selectedExamData} onFinishExam={handleFinishExam} onBack={() => localizedNavigate('/dashboard')} /></RedirectToExamScoped>} />
            <Route path="exam/instructions" element={<RedirectToExamScoped selectedExamData={selectedExamData} isLoadingExamData={isLoadingExamData} toPath="instructions"><ExamInstructionsPage examData={selectedExamData} onBeginExam={() => localizedNavigate('/exam/simulator')} onBack={() => localizedNavigate('/exam')} /></RedirectToExamScoped>} />
            <Route path="exam/results" element={<RedirectToExamScoped selectedExamData={selectedExamData} isLoadingExamData={isLoadingExamData} toPath="results"><ExamResultsPage examData={selectedExamData} results={examResults} onBack={() => localizedNavigate('/dashboard')} subscriptionStatus={subscriptionStatus} /></RedirectToExamScoped>} />
            <Route path="exam" element={<RedirectToExamScoped selectedExamData={selectedExamData} isLoadingExamData={isLoadingExamData} toPath=""><ExamDetailPage examData={selectedExamData} onStartPractice={handleStartPractice} onStartMockExam={handleStartMockExamVariant} onBack={() => localizedNavigate('/dashboard')} onNavigate={navigateTo} /></RedirectToExamScoped>} />

            <Route path="practice" element={<RedirectToExamScoped selectedExamData={selectedExamData} isLoadingExamData={isLoadingExamData} toPath="practice"><PracticePage examData={selectedExamData} user={user} personalizedPlan={personalizedPlan} onBack={() => localizedNavigate('/dashboard')} /></RedirectToExamScoped>} />
            <Route path="practice/vocabulary" element={<RedirectToExamScoped selectedExamData={selectedExamData} isLoadingExamData={isLoadingExamData} toPath="practice/vocabulary"><VocabularyPage onBack={() => localizedNavigate('/practice')} userLevel={selectedExamData?.level || 'B1'} /></RedirectToExamScoped>} />
            <Route path="practice/grammar" element={<RedirectToExamScoped selectedExamData={selectedExamData} isLoadingExamData={isLoadingExamData} toPath="practice/grammar"><GrammarPage onBack={() => localizedNavigate('/practice')} userLevel={selectedExamData?.level || 'B1'} /></RedirectToExamScoped>} />
            <Route path="lessons/:level" element={<RedirectToExamScoped selectedExamData={selectedExamData} isLoadingExamData={isLoadingExamData} toPath="lessons" fromPath="lessons"><GrammarLearningContainer user={user} isMobile={isMobile} onBack={() => localizedNavigate('/practice/grammar')} /></RedirectToExamScoped>} />

            <Route path="practice/redemittel" element={<RedirectToExamScoped selectedExamData={selectedExamData} isLoadingExamData={isLoadingExamData} toPath="practice/redemittel"><RedemittelPage onBack={() => localizedNavigate('/practice')} userLevel={selectedExamData?.level || 'B1'} examData={selectedExamData} /></RedirectToExamScoped>} />
            <Route path="practice/exam-section" element={<RedirectToExamScoped selectedExamData={selectedExamData} isLoadingExamData={isLoadingExamData} toPath="practice/exam-section"><ExamSectionPractice examData={selectedExamData} onBack={() => localizedNavigate('/practice')} /></RedirectToExamScoped>} />
            <Route path="practice/reading" element={<RedirectToExamScoped selectedExamData={selectedExamData} isLoadingExamData={isLoadingExamData} toPath="practice/reading"><LesenTrainingPage examData={selectedExamData} onBack={() => localizedNavigate('/practice')} subscriptionStatus={subscriptionStatus} isLoadingSubscription={isLoadingSubscription} /></RedirectToExamScoped>} />
            <Route path="practice/listening" element={<RedirectToExamScoped selectedExamData={selectedExamData} isLoadingExamData={isLoadingExamData} toPath="practice/listening"><HorenTrainingPage examData={selectedExamData} onBack={() => localizedNavigate('/practice')} subscriptionStatus={subscriptionStatus} isLoadingSubscription={isLoadingSubscription} /></RedirectToExamScoped>} />
            <Route path="practice/writing" element={<RedirectToExamScoped selectedExamData={selectedExamData} isLoadingExamData={isLoadingExamData} toPath="practice/writing"><SchreibenTrainingPage examData={selectedExamData} onBack={() => localizedNavigate('/practice')} subscriptionStatus={subscriptionStatus} isLoadingSubscription={isLoadingSubscription} /></RedirectToExamScoped>} />
            <Route path="study/grammar" element={<GrammarLearningContainer onBack={() => localizedNavigate('/study-plan')} userLevel={selectedExamData?.level || 'B1'} user={user} />} />
            <Route path="settings" element={<SettingsPage userProgress={userProgress} onLogout={handleLogout} />} />
            <Route path="profile/edit" element={<ProfileEditPage onNavigate={(path) => localizedNavigate(path)} onAvatarUpdate={handleAvatarUpdate} />} />
            <Route path="profile" element={<ProfilePage onNavigate={(path) => localizedNavigate(path)} />} />
            <Route path="subscription" element={<SubscriptionManagementPage />} />
            <Route path="consultations" element={<ConsultationBookingPage />} />
            <Route path="resources" element={<ResourceHubPage onBack={() => localizedNavigate('/dashboard')} />} />

            <Route path="study-plan" element={
              personalizedPlan ? (
                <StudyPlan
                  activePlan={personalizedPlan}
                  userProgress={userProgress}
                  onReset={() => localizedNavigate('/study-plan/wizard')}
                  onStartActivity={handleStartPractice}
                  onVariationChange={handlePlanVariationChange}
                  subscriptionStatus={subscriptionStatus}
                  isLoadingSubscription={isLoadingSubscription}
                />
              ) : (
                <Navigate to={`/${currentLang}/study-plan/wizard`} replace />
              )
            } />
            <Route path="study-plan/wizard" element={
              <StudyPlanWizard onPlanGenerated={handlePlanGenerated} onBack={() => localizedNavigate('/dashboard')} selectedExamData={selectedExamData} isAdmin={isAdmin} />
            } />

            {/* Exam-scoped SEO routes: /exams/:examSlug (auth + profile exam match enforced) */}
            <Route path="exams/:examSlug" element={<ExamSlugRoute selectedExamData={selectedExamData} />}>
              <Route index element={<ExamSlugDetailPage />} />
              <Route path="instructions" element={<ExamSlugInstructionsPage />} />
              <Route path="simulator" element={<ExamSlugSimulatorPage />} />
              <Route path="paper" element={<ExamSlugPaperPage />} />
              <Route path="results" element={<ExamSlugResultsPage />} />
              <Route path="retry/:setId" element={<ExamSlugRetryPage />} />
              <Route path="practice" element={<ExamSlugPracticePage />} />
              <Route path="practice/vocabulary" element={<ExamSlugVocabularyPage />} />
              <Route path="practice/grammar" element={<ExamSlugGrammarPage />} />
              <Route path="practice/redemittel" element={<ExamSlugRedemittelPage />} />
              <Route path="practice/exam-section" element={<ExamSlugExamSectionPractice />} />
              <Route path="practice/reading" element={<ExamSlugLesenTrainingPage />} />
              <Route path="practice/listening" element={<ExamSlugHorenTrainingPage />} />
              <Route path="practice/writing" element={<ExamSlugSchreibenTrainingPage />} />
              <Route path="lessons" element={<ExamSlugLessonsPage />} />
              <Route path="lessons/:level" element={<ExamSlugLessonsLevelPage />} />
              <Route path="lesson-strategy" element={<LessonStrategyPage />} />
            </Route>
            </Route>
          </Route>
          {/* Unknown path under /:lang -> real 404 page carrying robots noindex.
              Previously <Navigate to=".." />, which made every bogus URL a 200 OK copy
              of the homepage — an unbounded soft-404 surface Google was already
              indexing. See doc/SEO_MOCK_EXAM_RANKING_DIAGNOSIS_2026-08.md §4. */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        {/* Catch-all: e.g. /auth?mode=... or old links without :lang -> preserve path and query */}
        <Route path="*" element={<RedirectToDefaultLang />} />
      </Routes>
            </ErrorBoundary>
    </>

  );
}

export default App;